import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { generateLoadNumber } from "@/lib/generateLoadNumber";
import { getLocalTodayYMD } from "@/lib/dateOnly";
import { getTruckRoute } from "@/lib/mapbox";
import { voiceEquipmentToEnum } from "@/lib/lrDirect/voiceEquipment";

// Canonical home moved to src/lib/lrDirect/voiceEquipment.ts (pure, testable);
// re-exported here so existing imports keep working.
export { voiceEquipmentToEnum };

/**
 * LR Direct — voice post write path.
 *
 * Reuses the SHARED engine/utilities only (voice-post edge function for the
 * speech/parsing, `search_locations` RPC for geocoding, getTruckRoute for
 * mileage). No imports from the LaneRunner ATLAS hub / ai-post components.
 *
 * Mirrors useInstantPostFromTemplate: silent-write guard + broadcast fan-out.
 */

export interface VoiceDirectFields {
  origin_city?: string | null;
  origin_state?: string | null;
  destination_city?: string | null;
  destination_state?: string | null;
  equipment_type?: string | null;
  weight_lbs?: number | null;
  rate?: number | null;
  pickup_date?: string | null;
  commodity?: string | null;
  notes?: string | null;
  /** true when the speaker asked for their private network */
  private_network?: boolean | null;
}

export interface VoicePostResult {
  loadId: string;
  postType: "load" | "quote";
  pickupDate: string;
  visibility: "open_market" | "private_only";
  lane: string;
}

interface ResolvedCity {
  city: string;
  state: string;
  lat: number | null;
  lon: number | null;
}

/** Today 17:00 local, but never sooner than now + 6h (mirrors the DB trigger). */
function deriveBidDeadline(pickupYMD: string): string {
  const [y, m, d] = pickupYMD.split("-").map(Number);
  const atFive = new Date(y, m - 1, d, 17, 0, 0, 0).getTime();
  const floor = Date.now() + 6 * 60 * 60 * 1000;
  return new Date(Math.max(atFive, floor)).toISOString();
}

/**
 * Resolve a spoken "City, ST" through the shared local city DB so the post
 * gets coordinates + a canonical city/state. Falls back to the spoken text.
 */
export async function resolveSpokenCity(
  city: string,
  state?: string | null,
): Promise<ResolvedCity> {
  const query = state ? `${city}, ${state}` : city;
  try {
    const { data } = await supabase.rpc("search_locations", { q: query, lim: 5 });
    const rows = (data as any[]) || [];
    const match =
      (state && rows.find((r) => (r.state_code || "").toUpperCase() === state.toUpperCase())) ||
      rows[0];
    if (match) {
      return {
        city: match.city || city,
        state: (match.state_code || state || "").toUpperCase(),
        lat: match.lat ?? null,
        lon: match.lon ?? null,
      };
    }
  } catch (e) {
    console.warn("[voicePost] city resolve failed", query, e);
  }
  return { city, state: (state || "").toUpperCase(), lat: null, lon: null };
}

export function useVoicePostDirectLoad() {
  const qc = useQueryClient();

  return useMutation<
    VoicePostResult,
    Error,
    {
      fields: VoiceDirectFields;
      postType: "load" | "quote";
      userId: string;
      isDemo?: boolean;
    }
  >({
    mutationFn: async ({ fields, postType, userId, isDemo }) => {
      if (!fields.origin_city || !fields.destination_city || !fields.equipment_type) {
        throw new Error("Origin, destination and equipment are required to post.");
      }

      // 1. Geocode both sides via the shared local city search
      const [origin, destination] = await Promise.all([
        resolveSpokenCity(fields.origin_city, fields.origin_state),
        resolveSpokenCity(fields.destination_city, fields.destination_state),
      ]);
      if (!origin.state || !destination.state) {
        throw new Error("Could not resolve the state for one of the cities. Edit it and try again.");
      }

      // 2. Company + dates
      const { data: userCompanies } = await supabase.rpc("get_user_companies", { user_uuid: userId });
      const companyId = (userCompanies as string[] | null)?.[0] || null;

      const pickupDate = fields.pickup_date || getLocalTodayYMD();
      const bidDeadline = deriveBidDeadline(pickupDate);

      // 3. Visibility: private only when the shipper actually has a usable network
      let visibility: "open_market" | "private_only" = "open_market";
      let networkId: string | null = null;
      let maxBidders = 5;

      const { data: nets } = await supabase
        .from("shipper_private_networks" as any)
        .select("id, is_default, created_at")
        .eq("shipper_user_id", userId)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });
      const netRows = ((nets as any[]) || []) as { id: string }[];

      if (
        netRows.length &&
        fields.private_network !== false &&
        (fields.private_network === true || netRows.length === 1)
      ) {
        visibility = "private_only";
        networkId = netRows[0].id;
        const { count } = await supabase
          .from("shipper_network_brokers" as any)
          .select("id", { count: "exact", head: true })
          .eq("network_id", networkId)
          .eq("status", "accepted")
          .is("removed_at", null);
        maxBidders = Math.max(count ?? 0, 1);
      }

      // 4. Best-effort mileage (never blocks the post)
      let totalMiles: number | null = null;
      if (origin.lat && origin.lon && destination.lat && destination.lon) {
        try {
          const r = await getTruckRoute(
            [Number(origin.lon), Number(origin.lat)],
            [Number(destination.lon), Number(destination.lat)],
          );
          if (r?.distance) totalMiles = Math.round(r.distance);
        } catch (e) {
          console.warn("[voicePost] mileage compute failed", e);
        }
      }

      const equipmentEnum = voiceEquipmentToEnum(fields.equipment_type);
      const rate = typeof fields.rate === "number" && fields.rate > 0 ? fields.rate : null;

      const payload: Record<string, any> = {
        load_number: generateLoadNumber(),
        created_by: userId,
        company_id: companyId,
        status: "posted",
        is_demo: isDemo === true,
        origin_name: origin.city,
        origin_address: `${origin.city}, ${origin.state}`,
        origin_city: origin.city,
        origin_state: origin.state,
        origin_country: "US",
        origin_latitude: origin.lat,
        origin_longitude: origin.lon,
        destination_name: destination.city,
        destination_address: `${destination.city}, ${destination.state}`,
        destination_city: destination.city,
        destination_state: destination.state,
        destination_country: "US",
        destination_latitude: destination.lat,
        destination_longitude: destination.lon,
        pickup_date: pickupDate,
        delivery_date: null,
        equipment_type: equipmentEnum,
        weight_lbs: fields.weight_lbs ?? null,
        load_type: "FTL",
        load_nature: "spot",
        commodity: fields.commodity || null,
        special_instructions: fields.notes || null,
        total_miles: totalMiles,
        target_rate_min: rate,
        target_rate_max: rate,
        max_bidders: maxBidders,
        bid_deadline: bidDeadline,
        priority_tag: "standard",
        posted_at: new Date().toISOString(),
        appointment_type: "fcfs",
        handling_requirements: [],
        visibility_mode: visibility,
        post_type: postType,
        private_network_id: visibility === "private_only" ? networkId : null,
      };

      // 5. Silent-write guard: a returned row is the only proof it landed.
      const { data: newLoad, error } = await supabase
        .from("direct_loads" as any)
        .insert(payload)
        .select("id")
        .single();
      if (error) throw error;
      const loadId = (newLoad as any)?.id as string | undefined;
      if (!loadId) throw new Error("The post was not saved. Please try again.");

      // 6. Stops
      const stopRows = [
        {
          direct_load_id: loadId,
          stop_number: 1,
          stop_type: "pickup",
          name: origin.city,
          address: `${origin.city}, ${origin.state}`,
          city: origin.city,
          state: origin.state,
          country: "US",
          latitude: origin.lat,
          longitude: origin.lon,
          scheduled_date: pickupDate,
        },
        {
          direct_load_id: loadId,
          stop_number: 2,
          stop_type: "delivery",
          name: destination.city,
          address: `${destination.city}, ${destination.state}`,
          city: destination.city,
          state: destination.state,
          country: "US",
          latitude: destination.lat,
          longitude: destination.lon,
          scheduled_date: pickupDate,
        },
      ];
      const { data: insertedStops, error: stopsErr } = await supabase
        .from("direct_load_stops" as any)
        .insert(stopRows)
        .select("id");
      // Never swallow: a failed stop insert must surface as an error.
      if (stopsErr) throw new Error(`Load posted but stops failed to save: ${stopsErr.message}`);
      if ((insertedStops?.length ?? 0) !== stopRows.length) {
        throw new Error("Load posted but some stops did not save. Please check the load.");
      }

      // 7. Same broker fan-out as the post form / instant post
      if (postType === "quote") {
        supabase.functions
          .invoke("broadcast-new-quote-request", { body: { direct_load_id: loadId } })
          .catch((e) => console.warn("broadcast-new-quote-request failed", e));
      } else if (visibility === "private_only") {
        supabase.functions
          .invoke("broadcast-new-private-load", { body: { direct_load_id: loadId } })
          .catch((e) => console.warn("broadcast-new-private-load failed", e));
      } else {
        supabase.functions
          .invoke("broadcast-new-open-load", { body: { direct_load_id: loadId } })
          .catch((e) => console.warn("broadcast-new-open-load failed", e));
      }

      // ATLAS load analysis (bid loads only) — cached on the load row.
      if (postType !== "quote") {
        supabase.functions
          .invoke("atlas-direct-load-analysis", { body: { direct_load_id: loadId } })
          .catch((e) => console.warn("atlas-direct-load-analysis failed", e));
      }



      return {
        loadId,
        postType,
        pickupDate,
        visibility,
        lane: `${origin.city}, ${origin.state} → ${destination.city}, ${destination.state}`,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lr-direct-my-loads"] });
      qc.invalidateQueries({ queryKey: ["direct-quote-requests"] });
    },
  });
}
